// 处理ag-grid单元格选择
//
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { GridApi, GridOptions } from 'ag-grid-community';
import { noop } from 'lodash-es';
import { useClipboard } from '../../core/Common';

interface Position {
  row: number;
  col: number;
}

// 计算坐标
function getCellRowCol(
  container: HTMLElement | undefined,
  cell: HTMLElement
): Position | undefined {
  const row = cell.parentElement!;
  if (row.parentElement != container) return undefined;

  const rowIndex = parseInt(row.getAttribute('row-index')!);
  const colIndex = parseInt(cell.getAttribute('aria-colindex')!);

  return { row: rowIndex, col: colIndex };
}

function getCell(container: HTMLElement, row: number, col: number) {
  const rows = container.children;
  for (let i = 0; i < rows.length; i++) {
    const rowEl = rows.item(i) as HTMLElement;
    const rowIndex = parseInt(rowEl.getAttribute('row-index')!);
    if (rowIndex != row) {
      continue;
    }

    const cells = rowEl.children;
    for (let j = 0; j < cells.length; j++) {
      const cell = cells.item(j) as HTMLElement;
      const colIndex = parseInt(cell.getAttribute('aria-colindex')!);
      if (colIndex == col) {
        return cell;
      }
    }
  }

  return null;
}

// 清除选择
function clearSelection(
  container: HTMLElement | undefined,
  startPos: Position | undefined,
  endPos: Position | undefined
) {
  if (container && startPos && endPos) {
    const x0 = Math.min(startPos.col, endPos.col);
    const x1 = Math.max(startPos.col, endPos.col);
    const y0 = Math.min(startPos.row, endPos.row);
    const y1 = Math.max(startPos.row, endPos.row);

    for (let i = x0; i <= x1; i++) {
      for (let j = y0; j <= y1; j++) {
        const cell = getCell(container, j, i);
        if (!cell) continue;

        const classes = cell.classList;
        classes.remove('ag-cell-range-selected');

        if (i == x0 || i == x1 || j == y0 || j == y1) {
          classes.remove('ag-cell-range-left');
          classes.remove('ag-cell-range-right');
          classes.remove('ag-cell-range-top');
          classes.remove('ag-cell-range-bottom');
        }
      }
    }
  }
}

function setSelection(
  container: HTMLElement,
  startPos: Position,
  endPos: Position
) {
  const x0 = Math.min(startPos.col, endPos.col);
  const x1 = Math.max(startPos.col, endPos.col);
  const y0 = Math.min(startPos.row, endPos.row);
  const y1 = Math.max(startPos.row, endPos.row);

  for (let i = x0; i <= x1; i++) {
    for (let j = y0; j <= y1; j++) {
      const cell = getCell(container!, j, i);
      if (!cell) continue;

      const classes = cell.classList;
      classes.add('ag-cell-range-selected');

      if (i == x0 || i == x1 || j == y0 || j == y1) {
        if (x0 == x1) {
          classes.add('ag-cell-range-left');
          classes.add('ag-cell-range-right');
        } else if (i == x0) {
          classes.add('ag-cell-range-left');
        } else if (i == x1) {
          classes.add('ag-cell-range-right');
        }

        if (y0 == y1) {
          classes.add('ag-cell-range-top');
          classes.add('ag-cell-range-bottom');
        } else if (j == y0) {
          classes.add('ag-cell-range-top');
        } else if (j == y1) {
          classes.add('ag-cell-range-bottom');
        }
      }
    }
  }
}

function findInParent(target: HTMLElement | null, clazz: string) {
  if (!target || !target.classList) return null;

  if (target.classList.contains(clazz)) return target;

  return findInParent(target.parentElement, clazz);
}

function findAgCell(target: HTMLElement | null) {
  if (!target || !target.classList) return null;

  if (target.classList.contains('ag-cell')) return target;

  const child = target.querySelector('.ag-cell');
  if (child) return child;

  return findInParent(target.parentElement, 'ag-cell');
}

function onMouseOver(
  e: MouseEvent,
  container: HTMLElement,
  startPos: Position,
  endPos: Position
) {
  const target = findAgCell(e.target as HTMLElement);
  if (target) {
    const pos = getCellRowCol(container!, target as HTMLElement);
    if (!pos) {
      return endPos;
    }

    clearSelection(container, startPos, endPos);
    setSelection(container, startPos, pos);

    return pos;
  }

  return endPos;
}

function getSelectionText(
  container: HTMLElement | undefined,
  startPos: Position | undefined,
  endPos: Position | undefined
) {
  if (!container || !startPos || !endPos) {
    if (container) {
      const cell = container.querySelector('.ag-cell.ag-cell-focus');
      if (cell) {
        return cell.textContent ?? '';
      }

      const classList = container.classList;
      if (classList?.contains('ag-cell-focus')) {
        return container.textContent ?? '';
      }
    }

    return '';
  }

  const x0 = Math.min(startPos.col, endPos.col);
  const x1 = Math.max(startPos.col, endPos.col);
  const y0 = Math.min(startPos.row, endPos.row);
  const y1 = Math.max(startPos.row, endPos.row);

  const buffer = [] as string[];
  for (let j = y0; j <= y1; j++) {
    for (let i = x0; i <= x1; i++) {
      const cell = getCell(container!, j, i)!;
      buffer.push(cell.textContent ?? '');
      if (i < x1) {
        buffer.push('\t');
      }
    }

    if (j < y1) {
      buffer.push('\n');
    }
  }

  return buffer.join('');
}

function getCellElement(target: HTMLElement): HTMLElement | null {
  if (target.classList && target.classList.contains('ag-cell-value')) {
    return target;
  }

  const parent = target.parentElement;
  if (parent) {
    return getCellElement(parent);
  }

  return null;
}

export function useAgGridExt(
  { disabled = false },
  gridOptions: GridOptions<any>
) {
  // ag-grid ref
  const tableRef = ref();

  if (disabled) {
    return {
      tableRef,
      selectionText: () => '',
      clearSelection: noop,
      updateSelection: noop
    };
  }

  let isDown = false;
  let container: HTMLElement | undefined;
  let startPos: Position | undefined;
  let endPos: Position | undefined;

  function mouseOverHandle(e: MouseEvent) {
    if (isDown) {
      endPos = onMouseOver(e, container!, startPos!, endPos!);
    }
  }

  function mouseDownHandle(e: MouseEvent) {
    clearSelection(container, startPos, endPos);
    const target = getCellElement(e.target as HTMLElement);
    if (target) {
      container = target.parentElement!.parentElement!;
      startPos = getCellRowCol(container, target);
      endPos = undefined;
      isDown = true;
    }
  }

  function mouseUpHandle() {
    isDown = false;
  }

  function keyDownHandle(e: KeyboardEvent) {
    // ctrl + a
    if ((e.ctrlKey || e.metaKey) && e.key == 'a') {
      e.preventDefault();
      e.stopImmediatePropagation();

      gridApi.selectAll();
    }
  }

  function selectionText() {
    return getSelectionText(container, startPos, endPos);
  }

  function updateSelection() {
    if (container && startPos && endPos) {
      setSelection(container, startPos, endPos);
    }
  }

  function clear() {
    clearSelection(container, startPos, endPos);

    isDown = false;
    container = undefined;
    startPos = undefined;
    endPos = undefined;
  }

  // 取GridApi
  let gridApi: GridApi;
  const onGridReady = gridOptions.onGridReady;
  gridOptions.onGridReady = (params) => {
    onGridReady?.(params);
    gridApi = params.api;
  };

  // copy处理
  const { copy: copyClipboard } = useClipboard();
  const onCellKeyDown = gridOptions.onCellKeyDown;
  gridOptions.onCellKeyDown = (event) => {
    if (event.type == 'cellKeyDown') {
      const evt = event.event! as KeyboardEvent;
      if ((evt.ctrlKey || evt.metaKey) && evt.key == 'c') {
        const text = selectionText();
        copyClipboard(text);
      }
    }

    onCellKeyDown?.(event);
  };

  onMounted(() => {
    const table = tableRef.value.$el as HTMLElement;

    table.addEventListener('mouseover', mouseOverHandle);
    table.addEventListener('mousedown', mouseDownHandle);
    table.addEventListener('mouseup', mouseUpHandle);

    table.addEventListener('keydown', keyDownHandle);
  });

  onBeforeUnmount(() => {
    if (tableRef.value && tableRef.value.$el) {
      const table = tableRef.value.$el as HTMLElement;
      table.removeEventListener('mouseover', mouseOverHandle);
      table.removeEventListener('mousedown', mouseDownHandle);
      table.removeEventListener('mouseup', mouseUpHandle);

      table.removeEventListener('keydown', keyDownHandle);
    }
    container = undefined;
  });

  return { tableRef, selectionText, clearSelection: clear, updateSelection };
}

// 中文翻译
export const localeText = {
  applyFilter: '应用',
  clearFilter: '清除',
  resetFilter: '重置',
  cancelFilter: '取消',
  textFilter: '文本过滤',
  numberFilter: '数值过滤',
  dateFilter: '日期过滤',
  setFilter: '设置过滤',
  filterOoo: '过滤条件...',
  empty: '选择一个',
  equals: '等于',
  notEqual: '不等于',
  lessThan: '小于',
  greaterThan: '大于',
  inRange: '范围内',
  inRangeStart: '从',
  inRangeEnd: '到',
  lessThanOrEqual: '小于等于',
  greaterThanOrEqual: '大于等于',
  contains: '包含',
  notContains: '不包含',
  startsWith: '前缀为',
  endsWith: '后缀为',
  blank: '为空',
  notBlank: '不为空',
  andCondition: '并且',
  orCondition: '或者'
};
